import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { appraisalAdminService } from '../../../services/appraisalAdminService';
import type { ReviewDetail } from '../../../services/appraisalAdminService';
import { authService } from '../../../services';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import NoDataState from '../../../components/ui/NoDataState';
import ErrorState from '../../../components/ui/ErrorState';
import { deriveErrorState } from '../../../utils/errorStateUtils';

interface FeedbackData {
  id: number;
  reviewer: string;
  reviewerEmail?: string;
  capacity: string;
  section?: string;
  ratings?: {
    solutionFocused: number;
    adaptToWin: number;
    courageToAct: number;
    actForEnterprise: number;
    ownIt: number;
  };
  appreciativeFeedback?: string;
  constructiveFeedback?: string;
}

interface Props {
  employeeId?: string;
}

const TeamRatingsFeedbacks: React.FC<Props> = ({ employeeId }) => {
  const location = useLocation();
  const params = useParams();
  
  // State for API data
  const [superiorReviews, setSuperiorReviews] = useState<ReviewDetail[]>([]);
  const [hodReviews, setHodReviews] = useState<ReviewDetail[]>([]);
  const [nonSuperiorReviews, setNonSuperiorReviews] = useState<ReviewDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  // Get employee email from props, navigation state, or URL params
  const navState = (location.state || {}) as {
    employeeEmail?: string;
    employeeId?: string;
  };
  
  const loggedInEmail = authService.getStoredUser()?.email ?? null;
  const selectedEmployeeId = employeeId || navState.employeeId || params.employeeId;

  const fetchAllReviews = useCallback(async () => {
    if (!loggedInEmail) {
      setError('User email not available');
      setErrorStatus(undefined);
      setIsLoading(false);
      return;
    }

    if (!selectedEmployeeId) {
      setError('Employee ID not provided');
      setErrorStatus(undefined);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setErrorStatus(undefined);

      const targetEmployeeId = selectedEmployeeId as string;
      
      // Fetch all three types of reviews in parallel
      const [superiorResponse, hodResponse, nonSuperiorResponse] = await Promise.all([
        appraisalAdminService.getSuperiorReviewDetails(loggedInEmail, targetEmployeeId).catch(err => {
          console.error('Failed to fetch superior reviews:', err);
          return { data: [] };
        }),
        appraisalAdminService.getHODReviewDetails(loggedInEmail, targetEmployeeId).catch(err => {
          console.error('Failed to fetch HOD reviews:', err);
          return { data: [] };
        }),
        appraisalAdminService.getNonSuperiorReviewDetails(loggedInEmail, targetEmployeeId).catch(err => {
          console.error('Failed to fetch non-superior reviews:', err);
          return { data: [] };
        })
      ]);

      if (superiorResponse && superiorResponse.data && Array.isArray(superiorResponse.data)) {
        setSuperiorReviews(superiorResponse.data);
      } else {
        setSuperiorReviews([]);
      }

      if (hodResponse && hodResponse.data && Array.isArray(hodResponse.data)) {
        setHodReviews(hodResponse.data);
      } else {
        setHodReviews([]);
      }

      if (nonSuperiorResponse && nonSuperiorResponse.data && Array.isArray(nonSuperiorResponse.data)) {
        setNonSuperiorReviews(nonSuperiorResponse.data);
      } else {
        setNonSuperiorReviews([]);
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      let status: number | undefined;
      let message = 'Failed to load review data. Please try again.';

      if (err && typeof err === 'object') {
        if ('status_code' in err && typeof (err as { status_code?: unknown }).status_code === 'number') {
          status = (err as { status_code: number }).status_code;
        }
        if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
          message = (err as { message: string }).message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setErrorStatus(status);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInEmail, selectedEmployeeId]);

  useEffect(() => {
    fetchAllReviews();
  }, [fetchAllReviews]);

  const handleRetry = () => {
    if (!isLoading) {
      fetchAllReviews();
    }
  };

  // Helper function to map relationship type to display text
  const getCapacityText = (relationshipType: string): string => {
    const capacityMap: Record<string, string> = {
      'superior': 'Superior',
      'dotted_superior': 'Additional Superior',
      'peer': 'Peer',
      'subordinate': 'Subordinate',
      'additional_subordinate': 'Additional Subordinate',
      'self': 'Self'
    };
    return capacityMap[relationshipType] || relationshipType;
  };

  // Combine all reviews with section identifiers
  const allReviews: Array<ReviewDetail & { section: string }> = [
    ...superiorReviews.map(review => ({ ...review, section: 'Manager Review' })),
    ...hodReviews.map(review => ({ ...review, section: 'HOD Review' })),
    ...nonSuperiorReviews.map(review => ({ ...review, section: 'Team Member Review' }))
  ];

  // Transform API data to component format
  const transformedFeedbackData: (FeedbackData & { section: string })[] = allReviews.map((review) => ({
    id: review.review_id,
    reviewer: review.reviewer.name,
    reviewerEmail: review.reviewer.email,
    capacity: getCapacityText(review.relationship_type),
    section: review.section,
    ratings: {
      solutionFocused: review.competency.solution_focused,
      adaptToWin: review.competency.adapt_to_win,
      courageToAct: review.competency.courage_to_act,
      actForEnterprise: review.competency.act_for_enterprise,
      ownIt: review.competency.own_it,
    },
    appreciativeFeedback: review.feedback.appreciative,
    constructiveFeedback: review.feedback.constructive,
  }));

  const toggleExpand = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <LoadingSpinner className="mx-auto mb-4" />
          <p className="text-gray-600">Loading review data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const { type, title, message } = deriveErrorState(error, {
      fallbackMessage: error,
      statusCode: errorStatus,
    });
    return (
      <div className="py-12">
        <ErrorState
          type={type}
          title={title}
          message={message}
          onRetry={handleRetry}
          retryLabel={isLoading ? 'Retrying...' : 'Try Again'}
          showRetryButton
        />
      </div>
    );
  }

  // Show message if no reviews found
  if (transformedFeedbackData.length === 0) {
    return (
      <div className="py-12">
        <NoDataState
          title="No Reviews Found"
          description="No review data is available for this employee yet."
        />
      </div>
    );
  }

  // Group reviews by section
  const reviewsBySection: Record<string, typeof transformedFeedbackData> = {
    'Manager Review': transformedFeedbackData.filter(r => r.section === 'Manager Review'),
    'HOD Review': transformedFeedbackData.filter(r => r.section === 'HOD Review'),
    'Team Member Review': transformedFeedbackData.filter(r => r.section === 'Team Member Review')
  };

  return (
    <div className="w-full space-y-6">
      {Object.entries(reviewsBySection).map(([sectionName, sectionReviews]) => {
        if (sectionReviews.length === 0) return null;
        
        return (
          <div key={sectionName} className="space-y-4">
            {/* Section Header */}
            <div className="flex items-center gap-3 pb-2 border-b-2 border-purple-200">
              <h3 className="text-lg font-semibold text-purple-900">{sectionName}</h3>
              <span className="text-sm text-gray-600 bg-purple-100 px-3 py-1 rounded-full">
                {sectionReviews.length} {sectionReviews.length === 1 ? 'review' : 'reviews'}
              </span>
            </div>
            
            {/* Reviews in this section */}
            {sectionReviews.map((feedback) => (
              <div key={feedback.id} className="rounded-lg bg-white">
                {/* Header - Always visible */}
                <div 
                  className="flex cursor-pointer flex-col gap-3 rounded-lg bg-purple-50 p-4 transition-colors duration-150 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
                  onClick={() => toggleExpand(feedback.id)}
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-purple-600 font-medium">
                      Feedback by:{' '}
                      <NameWithEmailTooltip
                        as="span"
                        className="font-medium text-purple-700"
                        name={feedback.reviewer}
                        email={feedback.reviewerEmail}
                      />
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-purple-600 font-medium">
                      In the Capacity as: {feedback.capacity}
                    </span>
                    <svg 
                      className={`w-5 h-5 text-gray-400 transform transition-transform duration-200 ${
                        expandedCard === feedback.id ? 'rotate-180' : ''
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedCard === feedback.id && feedback.ratings && (
                  <div className="p-6">
                    {/* Ratings Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[720px] border-separate border-spacing-0 rounded-lg overflow-hidden">
                        <thead className='bg-purple-800'>
                          <tr className="text-white">
                            <th className="px-6 py-3 text-left text-base font-normal w-16 first:rounded-tl-lg first:rounded-bl-lg">S.NO.</th>
                            <th className="px-6 py-3 text-left text-base font-normal">Description/Rating</th>
                            <th className="px-6 py-3 text-center text-base font-normal">1-Excel</th>
                            <th className="px-6 py-3 text-center text-base font-normal">2-Fully Met</th>
                            <th className="px-6 py-3 text-center text-base font-normal">3-Mostly Met</th>
                            <th className="px-6 py-3 text-center text-base font-normal last:rounded-tr-lg last:rounded-br-lg">4-Requires Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[
                            { id: 1, description: 'Solution Focused', rating: feedback.ratings.solutionFocused },
                            { id: 2, description: 'Adapt to Win', rating: feedback.ratings.adaptToWin },
                            { id: 3, description: 'Courage to Act', rating: feedback.ratings.courageToAct },
                            { id: 4, description: 'Act for Enterprise', rating: feedback.ratings.actForEnterprise },
                            { id: 5, description: 'Own It', rating: feedback.ratings.ownIt }
                          ].map((criterion, index) => (
                            <tr key={criterion.id} className="bg-white hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">{index + 1}.</td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                <span>{criterion.description}</span>
                              </td>
                              {[1, 2, 3, 4].map((rating) => (
                                <td key={rating} className="px-6 py-4 text-center">
                                  <input
                                    type="radio"
                                    name={`rating-${criterion.id}-${feedback.id}`}
                                    value={rating}
                                    checked={criterion.rating === rating}
                                    disabled={true}
                                    readOnly={true}
                                    className="w-4 h-4 text-purple-600 border-gray-300 cursor-not-allowed opacity-60"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Feedback Sections */}
                    <div className="mt-8 space-y-6">
                      {/* Appreciative Feedback */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Add your <span className="font-semibold">Appreciative Feedback</span> for the Employee
                        </label>
                        <textarea
                          value={feedback.appreciativeFeedback}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm resize-none cursor-not-allowed"
                          rows={4}
                        />
                      </div>

                      {/* Constructive Feedback */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Add your <span className="font-semibold">Constructive Feedback</span> for the Employee
                        </label>
                        <textarea
                          value={feedback.constructiveFeedback}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-sm resize-none cursor-not-allowed"
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default TeamRatingsFeedbacks;
